<template>
  <div class="uploader-root">
    <div
      class="dropzone"
      :class="{
        'is-active': isDragging,
        'is-error': !!error,
        'is-loaded': !!file && !error,
      }"
      tabindex="0"
      role="button"
      :aria-label="ariaLabel"
      @click="openPicker"
      @keydown.enter.prevent="openPicker"
      @keydown.space.prevent="openPicker"
      @dragover.prevent="onDragOver"
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
    >
      <input
        ref="input"
        type="file"
        class="hidden-input"
        :accept="acceptAttr"
        @change="onChange"
      />

      <template v-if="!file && !error">
        <div class="icon">⬆</div>
        <p class="primary-text">{{ helperText || defaultHelper }}</p>
        <p class="secondary-text">
          {{ acceptedExtensions.map(e => '.' + e).join(', ') }} ·
          máx {{ maxSizeMb }} MB
        </p>
      </template>

      <template v-else-if="error">
        <div class="icon icon-error">!</div>
        <p class="primary-text" role="alert">{{ error }}</p>
        <button type="button" class="link-btn" @click.stop="reset">
          Intentar de nuevo
        </button>
      </template>

      <template v-else>
        <div class="icon icon-ok">✓</div>
        <p class="primary-text">{{ file.name }}</p>
        <p class="secondary-text">{{ humanSize }}</p>
        <button type="button" class="link-btn" @click.stop="reset">
          Cambiar archivo
        </button>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FileUploader',
  props: {
    acceptedExtensions: {
      type: Array,
      required: true,
    },
    acceptedMimeTypes: {
      type: Array,
      default: () => [],
    },
    maxSizeMb: {
      type: Number,
      default: 50,
    },
    helperText: {
      type: String,
      default: '',
    },
    ariaLabel: {
      type: String,
      default: 'Subir archivo',
    },
  },
  emits: ['file-selected', 'file-cleared', 'file-error'],
  data() {
    return {
      isDragging: false,
      file: null,
      error: '',
    };
  },
  computed: {
    acceptAttr() {
      const exts = this.acceptedExtensions.map((e) => '.' + e);
      return [...exts, ...this.acceptedMimeTypes].join(',');
    },
    defaultHelper() {
      return 'Arrastra un archivo aquí o haz clic para seleccionarlo';
    },
    humanSize() {
      if (!this.file) return '';
      const mb = this.file.size / (1024 * 1024);
      if (mb < 1) return `${(this.file.size / 1024).toFixed(1)} KB`;
      return `${mb.toFixed(2)} MB`;
    },
  },
  methods: {
    openPicker() {
      this.$refs.input.click();
    },
    onDragOver() {
      this.isDragging = true;
    },
    onDragLeave() {
      this.isDragging = false;
    },
    onDrop(e) {
      this.isDragging = false;
      const f = e.dataTransfer?.files?.[0];
      if (f) this.processFile(f);
    },
    onChange(e) {
      const f = e.target.files?.[0];
      if (f) this.processFile(f);
      // reset input so re-selecting same file fires change
      e.target.value = '';
    },
    processFile(file) {
      this.error = '';
      const ext = (file.name.split('.').pop() || '').toLowerCase();

      if (!this.acceptedExtensions.includes(ext)) {
        const msg = `Formato no permitido (.${ext}). Acepta: ${this.acceptedExtensions
          .map((e) => '.' + e)
          .join(', ')}.`;
        this.error = msg;
        this.file = null;
        this.$emit('file-error', msg);
        return;
      }

      if (
        this.acceptedMimeTypes.length > 0 &&
        file.type &&
        !this.acceptedMimeTypes.includes(file.type) &&
        // Algunos browsers reportan MIME vacío para .stl/.ply/.obj — no bloquear si vacío
        file.type !== ''
      ) {
        const msg = `Tipo MIME no reconocido (${file.type}).`;
        this.error = msg;
        this.file = null;
        this.$emit('file-error', msg);
        return;
      }

      const sizeMb = file.size / (1024 * 1024);
      if (sizeMb > this.maxSizeMb) {
        const msg = `Archivo demasiado grande (${sizeMb.toFixed(
          1
        )} MB). Máximo ${this.maxSizeMb} MB.`;
        this.error = msg;
        this.file = null;
        this.$emit('file-error', msg);
        return;
      }

      this.file = file;
      this.$emit('file-selected', file);
    },
    reset() {
      this.file = null;
      this.error = '';
      this.$emit('file-cleared');
    },
  },
};
</script>

<style scoped>
.uploader-root {
  width: 100%;
}

.dropzone {
  border: 2px dashed #9DB2BF;
  border-radius: 12px;
  background: white;
  padding: 2rem 1rem;
  text-align: center;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s;
  min-height: 250px;
  min-width: 250px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
}

.dropzone:hover,
.dropzone:focus-visible {
  border-color: #526D82;
  background: rgba(82, 109, 130, 0.04);
  outline: none;
}

.dropzone.is-active {
  border-color: #526D82;
  background: rgba(82, 109, 130, 0.08);
  border-style: solid;
}

.dropzone.is-loaded {
  border-color: #526D82;
  background: rgba(82, 109, 130, 0.04);
  border-style: solid;
}

.dropzone.is-error {
  border-color: #B91C1C;
  background: rgba(185, 28, 28, 0.04);
}

.hidden-input {
  display: none;
}

.icon {
  font-size: 2rem;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #526D82;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
}

.icon-error {
  background: #B91C1C;
}

.icon-ok {
  background: #16A34A;
}

.primary-text {
  margin: 0;
  color: #2C3E50;
  font-size: 1rem;
}

.secondary-text {
  margin: 0;
  color: #4F5665;
  font-size: 0.85rem;
}

.link-btn {
  background: none;
  border: none;
  color: #526D82;
  font-size: 0.9rem;
  cursor: pointer;
  text-decoration: underline;
  padding: 0.2rem 0.4rem;
}

.link-btn:hover {
  color: #3D566B;
}
</style>
